import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { flagIfAuthError } from '../../components/ResetBanner'

export type ReceiptItemInput = {
  request_id: string
  quantity_received?: number
}

export type RecordReceiptInput = {
  vendor_id?: string
  challan_number?: string
  challan_date?: number
  notes?: string
  items?: ReceiptItemInput[]
}

export type BillLineItemInput = {
  material_request_id: string
  basic_amount: number
  gst_rate?: '0' | '5' | '12' | '18' | '28'
  hsn_code?: string
}

export type CreateBillInput = {
  vendor_id: string
  bill_number: string
  bill_date: number
  gst_rate?: '0' | '5' | '12' | '18' | '28'
  gst_type?: 'intra' | 'inter'
  line_items: BillLineItemInput[]
  notes?: string
}

export function useLifecycleActions(batchId: string, projectId?: string, vendorId?: string | null) {
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['material-requests', projectId] })
    qc.invalidateQueries({ queryKey: ['material-requests', 'bulk', projectId] })
    qc.invalidateQueries({ queryKey: ['material-receipts', projectId] })
    qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] })
    if (vendorId) {
      qc.invalidateQueries({ queryKey: ['vendor-bills', vendorId] })
      qc.invalidateQueries({ queryKey: ['vendor-payments', vendorId] })
    }
  }

  const approve = useMutation({
    mutationFn: () =>
      fetchJson(orgPath(`/material-requests/batch/${batchId}/approve`), {
        method: 'POST',
      }),
    onError: flagIfAuthError,
    onSuccess: invalidate,
  })

  const reject = useMutation({
    mutationFn: (reason: string) =>
      fetchJson(orgPath(`/material-requests/batch/${batchId}/reject`), {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onError: flagIfAuthError,
    onSuccess: invalidate,
  })

  const recordReceipt = useMutation({
    mutationFn: (input: RecordReceiptInput) =>
      fetchJson(orgPath(`/material-receipts/batch/${batchId}`), {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: input.vendor_id || undefined,
          challan_number: input.challan_number || undefined,
          challan_date: input.challan_date,
          notes: input.notes || undefined,
          items: input.items?.length ? input.items : undefined,
        }),
      }),
    onError: flagIfAuthError,
    onSuccess: invalidate,
  })

  const createBill = useMutation({
    mutationFn: (input: CreateBillInput) =>
      fetchJson(orgPath(`/bills/batch/${batchId}`), {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: input.vendor_id,
          bill_number: input.bill_number,
          bill_date: input.bill_date,
          gst_rate: input.gst_rate,
          gst_type: input.gst_type ?? 'intra',
          line_items: input.line_items,
          notes: input.notes || undefined,
        }),
      }),
    onError: flagIfAuthError,
    onSuccess: invalidate,
  })

  return { approve, reject, recordReceipt, createBill }
}
